/**
 * /login — single-button federated sign-in.
 *
 * Visual reference: view 0 of the extension prototype at
 * `D&D/meetings/2026-04-23/artifacts/nucce_extension_preview_v17.html`.
 *
 * Uses `@nu-assist/ui` primitives (Card, BrandMark, GoogleButton,
 * InlineNote) built on top of Tailwind + the design tokens defined in
 * `tokens.css`. Default theme is dark (set via `data-theme="dark"` on
 * `<html>`; a future PR adds a user-opt-in light toggle).
 *
 * Behavior:
 *   - Mock mode (VITE_AUTH_PROVIDER=mock, default locally) → Dex /auth.
 *     Dex renders its own login form where the dev types email + the
 *     shared password (`nosecret`).
 *   - Google mode (VITE_AUTH_PROVIDER=google, dev/prod) → Google's
 *     authorization endpoint; Google's account picker handles user
 *     selection.
 *
 * Both paths:
 *   1. Generate CSRF `state`, stash in sessionStorage
 *   2. Build the authorization URL (provider-specific path)
 *   3. Redirect window.location to it
 *
 * On return at `/auth/callback?code=...&state=...`, the callback route
 * exchanges the code for our `.local.nu-assist.com` session cookie.
 */
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Card, BrandMark, GoogleButton, InlineNote } from "@nu-assist/ui";
import { useMe } from "@nu-assist/shared-fe";

const AUTH_PROVIDER = (import.meta.env.VITE_AUTH_PROVIDER ?? "mock") as
  | "mock"
  | "google";

const OIDC_ISSUER =
  import.meta.env.VITE_GOOGLE_OAUTH_ISSUER ??
  "https://oidc.local.nu-assist.com";

const OIDC_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ?? "nu-assist-local";

const STATE_KEY = "nu-assist:oauth-state";

function randomState(): string {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

function buildAuthorizeUrl(): string {
  const state = randomState();
  sessionStorage.setItem(STATE_KEY, state);

  const redirectUri = `${window.location.origin}/auth/callback`;
  const params = new URLSearchParams({
    client_id: OIDC_CLIENT_ID,
    response_type: "code",
    redirect_uri: redirectUri,
    scope: "openid email profile",
    state,
  });
  const authPath = AUTH_PROVIDER === "mock" ? "/auth" : "/o/oauth2/v2/auth";
  return `${OIDC_ISSUER}${authPath}?${params.toString()}`;
}

export function Login() {
  const me = useMe();
  const [pending, setPending] = useState(false);

  // Already-authenticated users land on /dashboard. AuthGuard handles
  // the reverse (no-user → /login); this is the symmetric guard so
  // bookmarks/manual nav to /login don't display a useless form.
  if (me.status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }
  // Brief blank during the initial useMe fetch — prevents a flash of
  // the login form for users who are actually authenticated.
  if (me.status === "loading") {
    return null;
  }

  const onLogin = () => {
    setPending(true);
    window.location.assign(buildAuthorizeUrl());
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4 font-sans text-text">
      <div className="w-full max-w-sm">
        {/* sr-only h1 for assistive tech + smoke-test heading-by-name match.
            The visible brand renders inside BrandMark below. */}
        <h1 className="sr-only">Sign in to NU Assist</h1>
        <Card>
          <BrandMark brand="NU ASSIST" caption="NU Advisory Partners" />

          <p className="text-center text-text-muted text-xs mt-1 mb-6">
            Sign in to NU Assist
          </p>

          <GoogleButton
            onClick={onLogin}
            loading={pending}
            data-testid="login-google"
          />

          <div data-testid="login-domain-restriction">
            <InlineNote>
              @nusearch.com accounts only · single sign-on with NU Assist web
            </InlineNote>
          </div>

          {AUTH_PROVIDER === "mock" && (
            <div
              data-testid="login-dev-note"
              className="mt-4 px-3 py-2 bg-accent-muted rounded-sm text-micro font-normal text-text-secondary leading-relaxed"
            >
              <strong className="text-text">Dev mode</strong> — on the next
              screen enter your <code>@nusearch.com</code> email plus password{" "}
              <code>nosecret</code>.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
